/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import {
  Shield, Trophy, Target, ArrowRight, CheckCircle2, Lock, Sparkles,
  AlertCircle, FileText, Download, BarChart3, Award, RefreshCw, Mail, Loader2,
} from "lucide-react";
import { ClinqLogo } from "../components/ClinqLogo";
import WebReportSections from "../components/founder/WebReportSections";
import { CFR_FULL, CFR_SHORT } from "../lib/brand";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------- SAMPLE DATA
// Realistic but obviously-not-real demo company so founders can see the shape
// of the unlocked report without us having to hand-write per-founder content.
const SAMPLE = {
  company_name: "Acme AgriTech (sample company)",
  sector: "Agribusiness",
  country: "Kenya",
  stage: "Seed",
  revenue_annual: 180_000,
  employees: 11,
  score: 67,
  target: 70,
  summary:
    "Acme AgriTech is a strong early-stage agritech with a defensible distribution model and clean financial discipline, but pre-Series-A funders will want a clearer 18-month growth plan and tighter unit economics before writing a cheque.",
  headline: "You're 3 points away from Market Ready (70+), when funder introductions start.",
  // PDF-parity fields
  pillar_scores: { P1: 72, P2: 78, P3: 65, P4: 80, P5: 55, P6: 60 },
  pillar_weights_used: { P1: 0.25, P2: 0.25, P3: 0.15, P4: 0.20, P5: 0.15, P6: 0.0 },
  funder_type_used: "seed_vc",
  recommended_structure: {
    primary_type: "equity",
    secondary_type: "revenue_based_finance",
    rationale: "You have clear growth signals (38% MoM) and a credible team — equity Seed VCs will be the highest-leverage capital. Revenue-based finance is a strong non-dilutive backup once you cross $20K MRR.",
  },
  capital_types_viable: ["equity", "revenue_based_finance", "grant", "debt"],
  funder_match_list: [
    { funder_category: "Seed / Pre-Series A VC", fit_score: 78,
      match_reasons: ["Founding team has 12+ years sector experience",
                       "Real traction: 3,400 paying farmers and 38% MoM growth",
                       "Distribution model is repeatable across counties"],
      gaps: ["Cap table has 4 uncapped SAFEs — will be flagged in due diligence",
             "Unit economics not yet documented"] },
    { funder_category: "Agritech / Impact Angels", fit_score: 72,
      match_reasons: ["Story fits angel cheque size ($25K–$250K)",
                       "Smallholder farmer impact angle is investable"],
      gaps: ["Need a 90-second video pitch + refreshed deck"] },
    { funder_category: "Revenue-Based Finance", fit_score: 58,
      match_reasons: ["Recurring monthly revenue base is real",
                       "No dilution gives you optionality"],
      gaps: ["Need 6+ months of clean bank statements + 3-month forward forecast"] },
  ],
  roadmap: [
    { milestone: "Document unit economics (CAC, LTV, gross margin, payback period) in one page",
      timeframe: "Week 1–2", capital_impact: "Removes the #1 question every Seed VC asks before they read the deck" },
    { milestone: "Convert uncapped SAFEs to capped notes",
      timeframe: "Week 3–4", capital_impact: "Cleans up the cap table — investors won't have to negotiate around legacy paper" },
    { milestone: "Build an 18-month financial forecast with stated assumptions",
      timeframe: "Week 4–6", capital_impact: "Lets funders pressure-test your growth plan and set ticket size confidently" },
    { milestone: "Sign 2 distribution partnerships with regional cooperatives",
      timeframe: "Week 6–10", capital_impact: "Reduces top-3 customer concentration below 30% and proves repeatable growth" },
    { milestone: "Record a 90-second video pitch and refresh the deck",
      timeframe: "Week 8", capital_impact: "Halves the time it takes to get to a first investor meeting" },
    { milestone: "Pre-target 8 Seed VCs (agritech / impact-led) with warm intros",
      timeframe: "Week 10–12", capital_impact: "Builds your investor pipeline before you formally open the round" },
  ],
  strengths: [
    "Clean monthly management accounts — funders can audit revenue trail in under 10 minutes.",
    "Strong founding team with sector expertise (12+ years combined in agribusiness).",
    "Repeatable distribution model — already operating across 4 counties with consistent margin.",
    "Validated demand: 3,400+ paying smallholder farmers and 38% MoM organic growth.",
  ],
  weaknesses: [
    "Unit economics aren't yet documented (CAC, LTV, payback period missing).",
    "No 18-month financial projection with stated assumptions — most Seed/Series A funders ask for one in week one.",
    "Cap table needs cleanup: 4 angel SAFEs are uncapped — likely to flag in due diligence.",
    "Customer concentration risk: top 3 clients = 47% of revenue.",
  ],
  funder_sources: [
    { id: "angels", label: "Angel Investors",
      blurb: "High-net-worth individuals. $5K-$250K per cheque, strategic mentorship.",
      likelihood: "high",
      rationale: "Angels lean into seed-stage stories with a clear thesis and traction signals." },
    { id: "mfis", label: "Microfinance Institutions",
      blurb: "Smaller loans for working capital, often unsecured. Higher rates than banks.",
      likelihood: "high",
      rationale: "MFIs specifically serve SMEs in your revenue / team band." },
    { id: "vcs", label: "Venture Capital (VCs)",
      blurb: "Institutional funds chasing 10× returns. Tech, scalable, high-growth bias.",
      likelihood: "medium",
      rationale: "Non-tech VCs (impact, climate, agritech-focused) may fit." },
    { id: "banks", label: "Banks",
      blurb: "Debt facilities. Want positive cash flow + collateral. No equity dilution.",
      likelihood: "medium",
      rationale: "Banks may approve a working-capital line; collateral will be required." },
    { id: "family_friends", label: "Family & Friends",
      blurb: "Personal network. Smallest cheques, fastest decisions, hardest to refuse.",
      likelihood: "low",
      rationale: "Once you have material revenue, professional funders are a faster fit." },
    { id: "pe", label: "Private Equity (PEs)",
      blurb: "Institutional buyers of profitable, cash-generative businesses. $5M+ cheques.",
      likelihood: "low",
      rationale: "PE funds rarely look below $1M ARR; come back when you cross that line." },
  ],
  checklist_groups: [
    {
      category: "Financial Discipline",
      items: [
        { id: "fin1", title: "Build a 12-month cash-flow forecast (template provided)", impact: "+3 pts", effort: "2-3 days" },
        { id: "fin2", title: "Document unit economics: CAC, LTV, gross margin, payback", impact: "+4 pts", effort: "1 day" },
        { id: "fin3", title: "Get 2 most recent quarters audited by a Big-4-tier firm", impact: "+2 pts", effort: "4 weeks" },
      ],
    },
    {
      category: "Governance & Cap Table",
      items: [
        { id: "gov1", title: "Convert uncapped SAFEs to capped notes (legal template available)", impact: "+2 pts", effort: "2 weeks" },
        { id: "gov2", title: "Form an advisory board with 2 sector experts", impact: "+1 pt",  effort: "3 weeks" },
      ],
    },
    {
      category: "Commercial Traction",
      items: [
        { id: "com1", title: "Reduce top-3 customer concentration below 30% of revenue", impact: "+3 pts", effort: "2-3 months" },
        { id: "com2", title: "Sign 2 distribution partnerships with regional cooperatives", impact: "+2 pts", effort: "6 weeks" },
      ],
    },
    {
      category: "Pitch Materials",
      items: [
        { id: "pit1", title: "Refresh deck — current version missing competitive landscape slide", impact: "+1 pt",  effort: "1 day" },
        { id: "pit2", title: "Record a 90-second video pitch (Clinq template provided)", impact: "+1 pt",  effort: "1 day" },
      ],
    },
  ],
};

const _LIKELIHOOD_ORDER = { high: 0, medium: 1, low: 2 };
const SAMPLE_SORTED_FUNDER_SOURCES = [...SAMPLE.funder_sources].sort(
  (a, b) => _LIKELIHOOD_ORDER[a.likelihood] - _LIKELIHOOD_ORDER[b.likelihood],
);

const _SCORE_BANDS = [
  { min: 85, label: "Capital-Ready",     color: "#00828F", bg: "bg-emerald-50", text: "text-emerald-700" },
  { min: 70, label: "Market Ready",      color: "#00828F", bg: "bg-emerald-50", text: "text-emerald-700" },
  { min: 55, label: "Improving",         color: "#ECC764", bg: "bg-amber-50",   text: "text-amber-700" },
  { min: 0,  label: "Early stage",       color: "#dc2626", bg: "bg-red-50",     text: "text-red-700" },
];
const SCORE_BAND = (s) => _SCORE_BANDS.find((b) => s >= b.min) || _SCORE_BANDS[_SCORE_BANDS.length - 1];

const _LIKELIHOOD_CLS = {
  high:   { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500", label: "High" },
  medium: { bg: "bg-amber-100",   text: "text-amber-800",   dot: "bg-amber-500",   label: "Medium" },
  low:    { bg: "bg-neutral-100", text: "text-neutral-600", dot: "bg-neutral-400", label: "Low" },
};
const _likelihoodCls = (likelihood) => _LIKELIHOOD_CLS[likelihood] || _LIKELIHOOD_CLS.low;

// ---------------------------------------------------------------- PAGE
export default function SampleReadinessReport() {
  const band = SCORE_BAND(SAMPLE.score);
  const pct = (SAMPLE.score / 100) * 100;

  return (
    <div className="min-h-screen bg-[#E5EEF1]">
      {/* Top bar */}
      <header className="border-b border-neutral-200 bg-white px-6 py-4">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <Link to="/" data-testid="sample-back-home">
            <ClinqLogo />
          </Link>
          <Link to="/get-started" data-testid="sample-signup-cta"
                className="group inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-4 py-2 text-sm font-bold text-white shadow-md ring-1 ring-emerald-300/0 transition-all hover:bg-[#00303F] hover:ring-emerald-300/60 hover:shadow-emerald-400/20">
            Get my real report
            <ArrowRight className="h-4 w-4 text-emerald-300 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </header>

      {/* Sample banner */}
      <div className="border-b border-amber-200 bg-amber-50 px-6 py-3">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            <Lock className="h-3 w-3" /> Sample
          </span>
          <p className="flex-1 min-w-[200px] text-amber-900">
            This is a sample {CFR_SHORT} report for a fictional company.
            Your real report will analyze <b>your business</b> using your uploaded documents, financials, and team profile.
          </p>
          <a href={`${BACKEND}/api/public/sample-report.pdf`}
             download="clinq-sample-ccr-report.pdf"
             data-testid="sample-download-pdf"
             className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700">
            <Download className="h-3.5 w-3.5" /> Download sample PDF
          </a>
        </div>
      </div>

      <main className="mx-auto max-w-5xl px-6 py-10" data-testid="sample-report-main">
        {/* Company chip + score header */}
        <section className="rounded-2xl border border-neutral-200 bg-white p-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#00828F]">{CFR_FULL}</p>
              <h1 className="mt-1 text-2xl font-extrabold text-neutral-900 sm:text-3xl" data-testid="sample-company-name">
                {SAMPLE.company_name}
              </h1>
              <p className="mt-1 text-sm text-neutral-500">
                {SAMPLE.sector} · {SAMPLE.country} · {SAMPLE.stage} · {SAMPLE.employees} employees · ${SAMPLE.revenue_annual.toLocaleString()} ARR
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${band.bg} ${band.text}`}>
                <Award className="h-3.5 w-3.5" /> {band.label}
              </span>
            </div>
          </div>

          {/* Score ring */}
          <div className="mt-6 flex flex-wrap items-center gap-8">
            <div className="relative h-32 w-32 shrink-0" data-testid="sample-score-ring">
              <svg viewBox="0 0 36 36" className="h-32 w-32 -rotate-90">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke="#e5e7eb" strokeWidth="3" />
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke={band.color} strokeWidth="3"
                      strokeDasharray={`${pct},100`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-extrabold text-neutral-900">{SAMPLE.score}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">/ 100</span>
              </div>
            </div>
            <div className="flex-1 min-w-[260px]">
              <p className="text-sm font-bold text-neutral-900">{SAMPLE.headline}</p>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{SAMPLE.summary}</p>
              <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-neutral-500">
                <Target className="h-3.5 w-3.5 text-[#ECC764]" />
                Target for {SAMPLE.stage}-stage funders: <b className="text-neutral-800">{SAMPLE.target}+</b>
              </div>
            </div>
          </div>
        </section>

        {/* PDF-parity sections — same structure as the downloaded report */}
        <div className="mt-6" data-testid="sample-web-sections">
          <WebReportSections data={{
            readiness_score: SAMPLE.score,
            summary: SAMPLE.summary,
            strengths: SAMPLE.strengths,
            weaknesses: SAMPLE.weaknesses,
            pillar_scores: SAMPLE.pillar_scores,
            pillar_weights_used: SAMPLE.pillar_weights_used,
            funder_type_used: SAMPLE.funder_type_used,
            recommended_structure: SAMPLE.recommended_structure,
            capital_types_viable: SAMPLE.capital_types_viable,
            funder_match_list: SAMPLE.funder_match_list,
            roadmap: SAMPLE.roadmap,
          }}/>
        </div>

        {/* Get this report by email + drip nurture */}

        {/* What you get */}
        <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00828F]">In your Capital Readiness Report you also get</p>
          <h2 className="mt-1 text-xl font-bold text-neutral-900">Everything above + 8 deeper analyses unique to your business</h2>
          <p className="mt-1 text-sm text-neutral-500">
            The sample above shows the structure. The full report dives into <b>your</b> data and adds the locked items below.
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <LockedItem icon={BarChart3} title="Sector & stage benchmark"
              body="See exactly where your CCR Score sits vs peers in your sector and stage. e.g. 'Top 15% of seed-stage agritech in East Africa.'" />
            <LockedItem icon={Target} title="18-month roadmap to Series A"
              body="Quarter-by-quarter milestones, hiring plan and funding events tailored to your traction." />
            <LockedItem icon={Award} title="Top 10 matched funders"
              body="A curated list of funders whose mandate matches your stage / sector / ticket size — with intro priority." />
            <LockedItem icon={FileText} title="Investor narrative"
              body="A paste-ready 2-paragraph pitch written from your data — perfect for cold investor emails." />
            <LockedItem icon={Sparkles} title="Pitch deck slide-by-slide review"
              body="Specific feedback on every slide of your uploaded deck — what to cut, add or rewrite before sending." />
            <LockedItem icon={Shield} title="Verified Clinq Certificate (≥ 70)"
              body="Cross 70 — Market Ready — and you unlock a stamped, verifiable certificate you can share with funders to prove readiness." />
            <LockedItem icon={Mail} title="Outreach email templates"
              body="3 proven email templates (warm intro, cold pitch, follow-up) pre-filled with your business details." />
            <LockedItem icon={RefreshCw} title="3 re-runs + progress tracking"
              body="Each purchase includes 3 full re-runs so you can track your CCR Score climb over time." />
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="mt-8 rounded-2xl border border-[#00828F]/30 bg-[#00828F]/5 p-7 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00828F]">Ready for your own?</p>
          <h2 className="mt-2 text-2xl font-extrabold text-neutral-900">
            Get your real {CFR_SHORT} report in under 5 minutes.
          </h2>
          <p className="mt-2 text-sm text-neutral-600 max-w-xl mx-auto">
            Sign up free, get your report, and we will generate a Capital Readiness Report tailored to <i>your</i> business —
            same structure, your data.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <Link to="/get-started" data-testid="sample-cta-signup"
                  className="group inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-6 py-3 text-sm font-bold text-white shadow-md ring-1 ring-emerald-300/0 transition-all hover:bg-[#00303F] hover:ring-emerald-300/60 hover:shadow-emerald-400/20">
              Get my real report <ArrowRight className="h-4 w-4 text-emerald-300 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </section>

        <p className="mt-6 text-center text-xs text-neutral-500">
          Sample data shown above is illustrative. Your real report is generated from your uploaded financials,
          pitch deck, and team profile.
        </p>
      </main>
    </div>
  );
}

function Benefit({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-3 rounded-lg border border-neutral-100 bg-neutral-50/40 p-4">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#00828F]/10 text-[#00828F]">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="text-sm font-bold text-neutral-900">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-neutral-600">{body}</p>
      </div>
    </div>
  );
}

function LockedItem({ icon: Icon, title, body }) {
  return (
    <div className="relative flex gap-3 rounded-lg border border-neutral-200 bg-gradient-to-br from-white to-neutral-50 p-4">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#00828F]/10 text-[#00828F]">
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-bold text-neutral-900">{title}</p>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700">
            <Lock className="h-2.5 w-2.5" /> Locked
          </span>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-neutral-600">{body}</p>
      </div>
    </div>
  );
}
